/** @jsx jsx */
import React from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { jsx, Box, Container, Flex, Grid, Heading } from "theme-ui"
import Action from "../../shared/Action"
import EnhanceMarkup from "../../shared/EnhanceMarkup"
import StatsItem from "./StatsItem"
import ContentfulRichText from "../../shared/ContentfulRichText"
import useBorderStyles from "../../../hooks/useBorderStyle"
import WistiaEmbed from "../../shared/WistiaEmbed"

const SingleImageLayout = ({ sectionContent, smallScreen }) => {
  const {
    content,
    fontSize,
    h1Heading,
    h2Heading,
    h3Heading,
    h4Heading,
    displayImageOnMobile,
    image,
    imagePosition,
    outOfTheBoxImage,
    textAlign,
    statsBlock,
    imageFullRight,
    buttons,
    backgroundColor,
    imageWidth,
    fontColor,
    imageContainerWidth,
    textContainerWidth,
    verticalPaddings,
    classname,
    wistiaItem,
  } = sectionContent

  const borderStyle = useBorderStyles(classname)
  const defaultPadding = "60px 0 60px 0"

  let contentLayout

  switch (imagePosition) {
    case "right":
      contentLayout = "row"
      break
    case "bottom":
      contentLayout = "column"
      break
    case "left":
      contentLayout = "row-reverse"
      break
    case "top":
      contentLayout = "column-reverse"
      break
    default:
      contentLayout = "row"
      break
  }

  let buttonsAlignment

  switch (textAlign) {
    case "right":
      buttonsAlignment = "flex-end"
      break
    case "left":
      buttonsAlignment = "flex-start"
      break
    case "center":
      buttonsAlignment = "center"
      break
    default:
      buttonsAlignment = "left"
      break
  }

  return (
    <Box
      sx={{
        backgroundColor: backgroundColor && backgroundColor,
        marginInline: "auto",
        overflow: "hidden",
        position: imageFullRight ? "relative" : "",
      }}
      className={classname}
    >
      <Container className="hero-main-container">
        <Flex
          sx={{
            flexDirection: ["column", "column", "column", contentLayout],
            justifyContent: [
              "center",
              textAlign === "left" ? "flex-start" : "center",
              wistiaItem
                ? "space-around"
                : imagePosition === "left" || imagePosition === "right"
                ? "space-between"
                : imagePosition === "left"
                ? "flex-start"
                : "center",
            ],
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              maxWidth: ["100%", "100%", null, textContainerWidth || "50%"],
              width: ["100%", "100%", null, "auto"],
              textAlign,
              margin: [
                displayImageOnMobile && image && !outOfTheBoxImage && "0 auto 30px",
                imageFullRight && image && "0",
              ],
              flexGrow: 1,
              padding: [
                "0 0 50px 0",
                verticalPaddings ? `${verticalPaddings}px 0 ${verticalPaddings}px 0` : defaultPadding,
              ],
            }}
            className="hero-comp"
          >
            {smallScreen && (image || wistiaItem) && (
              <Box
                sx={{
                  width: wistiaItem ? "100%" : ["fit-content", "fit-content", "fit-content", "100%"],
                  display: [!displayImageOnMobile && "none", "block"],
                  marginInline: "auto",
                  justifyContent: "center",
                  my: "20px",
                }}
                className="hero-comp-image"
              >
                {image && !wistiaItem && (
                  <>
                    {image.file.contentType === "video/mp4" ? (
                      <video
                        autoPlay="autoplay"
                        loop="loop"
                        playsInline
                        muted
                        width="100%"
                        height="100%"
                        style={borderStyle ? { ...borderStyle } : null}
                      >
                        <source src={image.file.url} type="video/mp4" />
                      </video>
                    ) : (
                      <GatsbyImage
                        image={image.gatsbyImageData}
                        alt={image.description}
                        sx={{
                          display: [!displayImageOnMobile && "none", "block"],
                          maxWidth: imageWidth || "100%",
                        }}
                        style={borderStyle ? { ...borderStyle } : null}
                      />
                    )}
                  </>
                )}
                {wistiaItem && !image && wistiaItem.videos && wistiaItem.videos.items.length > 0 && (
                  <Box>
                    {wistiaItem.wistiaHeading && <h3>{wistiaItem.wistiaHeading}</h3>}
                    <Box className="wistia-all-wrapper">
                      {wistiaItem.videos?.items?.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            width: "100%",
                            mb: 4,
                            ...(borderStyle || {}),
                          }}
                        >
                          <WistiaEmbed hashedId={item.hashed_id} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            {h1Heading && (
              <Heading
                as="h1"
                variant="extraBold"
                marginBottom="32px"
                sx={{ color: fontColor, paddingTop: ["20px", "0"] }}
              >
                <EnhanceMarkup text={h1Heading} />
              </Heading>
            )}
            {h2Heading && (
              <Heading
                as="h1"
                variant="extraBold"
                marginBottom="30px"
                sx={{ color: fontColor, paddingTop: ["20px", "0"], fontSize: ["32px", "48px"] }}
              >
                <EnhanceMarkup text={h2Heading} />
              </Heading>
            )}
            {h3Heading && (
              <Heading as="h3" variant="bold" marginBottom="8px" sx={{ color: fontColor, paddingTop: [20, 0] }}>
                {h3Heading}
              </Heading>
            )}
            {h4Heading && (
              <>
                <Heading as="h4" variant="bold" marginBottom="8px" sx={{ color: fontColor, paddingTop: [20, 0] }}>
                  {h4Heading}
                </Heading>
                <br />
              </>
            )}
            {content && (
              <Box
                className="hero-content"
                sx={{
                  fontFamily: "fonts.fontSource",
                  textAlign: [textAlign || "left", textAlign && textAlign],
                  paddingTop: [15, "unset"],
                }}
              >
                <ContentfulRichText richText={content} textfontSize={fontSize} fontColor={fontColor} />
              </Box>
            )}
            {buttons && (
              <Flex
                sx={{
                  width: "100%",
                  flexDirection: ["column", "column", "column", "row"],
                  justifyContent: ["center", buttonsAlignment],
                  alignItems: ["center", buttonsAlignment, "center"],
                  flexWrap: "wrap",
                }}
              >
                {buttons?.map((button) => {
                  if (!button.fullWidth) {
                    return (
                      <Box mt={4} className="hero-action-item-container" sx={{ marginRight: "25px" }}>
                        <Action
                          {...button}
                          fontColor={fontColor}
                          classnameForAnalytics={button.classnameForAnalytics}
                          paddingCustom={button.style === "button secondaryV2" ? ["5px 20px", "5px 20px 5px 0"] : null}
                          width={button.style === "button secondaryV2" ? "auto" : "max-content"}
                        />
                      </Box>
                    )
                  }
                  return <></>
                })}
              </Flex>
            )}
          </Box>
          {!smallScreen && (image || wistiaItem) && (
            <Flex
              sx={{
                width: ["100%", "100%", null, imageContainerWidth || "50%"],
                maxWidth: textContainerWidth - imageContainerWidth || "50%",
                textAlign: "-webkit-center",
                paddingY: ["50px", "60px"],
                justifyContent: "center",
                ...(outOfTheBoxImage
                  ? {
                      justifyContent: "flex-start",
                      alignSelf: "stretch",
                      paddingY: 0,
                    }
                  : {
                      paddingY: ["50px", "60px"],
                    }),
              }}
            >
              {!wistiaItem && image && (
                <>
                  {image.file.contentType === "video/mp4" ? (
                    <video
                      autoPlay
                      loop
                      playsInline
                      muted
                      width="100%"
                      height="125%"
                      style={borderStyle ? { ...borderStyle } : null}
                    >
                      <source src={image.file.url} type="video/mp4" />
                    </video>
                  ) : outOfTheBoxImage ? (
                    <GatsbyImage
                      image={image.gatsbyImageData}
                      alt={image.title || "hero image"}
                      sx={{ overflow: "visible", height: "100%" }}
                      className="hero-comp-image--out-of-the-box"
                      imgStyle={{ width: "auto" }}
                      style={borderStyle ? { ...borderStyle } : null}
                    />
                  ) : (
                    <GatsbyImage
                      sx={{
                        maxWidth: imageWidth || "100%",
                      }}
                      image={image.gatsbyImageData}
                      alt={image.title || "hero image"}
                      style={borderStyle ? { ...borderStyle } : null}
                    />
                  )}
                </>
              )}
              {wistiaItem && !image && wistiaItem.videos && wistiaItem.videos.items.length > 0 && (
                <Box
                  sx={{ width: wistiaItem ? "100%" : ["fit-content", "fit-content", "fit-content", "100%"] }}
                  className="hi"
                >
                  {wistiaItem.wistiaHeading && <h3>{wistiaItem.wistiaHeading}</h3>}
                  <Box className="wistia-all-wrapper">
                    {wistiaItem.videos?.items?.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: "100%",
                          mb: 4,
                          ...(borderStyle || {}),
                        }}
                      >
                        <WistiaEmbed hashedId={item.hashed_id} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Flex>
          )}
        </Flex>
        {buttons && (
          <Flex sx={{ justifyContent: "center" }}>
            <Flex
              sx={{ width: "100%", justifyContent: ["center", "flex-start", null, "center"], alignItems: "center" }}
            >
              {buttons?.map((button) => {
                if (button.fullWidth) {
                  return (
                    <Box mt={2} sx={{ marginLeft: ["0", "20px", "40px", "0"] }}>
                      <Action
                        {...button}
                        fontColor={fontColor}
                        wordWrap={true}
                        fontsize={["12px", "18px"]}
                        classnameForAnalytics={button.classnameForAnalytics}
                      />
                    </Box>
                  )
                }
                return <></>
              })}
            </Flex>
          </Flex>
        )}
        {statsBlock && (
          <Grid
            gap={0}
            columns={[1, 1, 2, statsBlock.length || 4]}
            sx={{ color: "#FD5383", mt: ["0", "0", "24px", "76px"] }}
          >
            {statsBlock.map((statsItem) => (
              <StatsItem
                key={statsItem}
                statsItem={statsItem}
                sx={{
                  borderRight: ["none", "none", "none", "default"],
                  px: ["0", "0", "0", "90px"],
                  mb: ["24px", "24px", "24px", "0"],
                  "&:first-of-type": { pl: "0" },
                  "&:last-of-type": { borderRight: "none", pr: "0" },
                }}
              />
            ))}
          </Grid>
        )}
      </Container>
      {!smallScreen && imageFullRight && image && (
        <Box
          sx={{
            position: "absolute",
            top: "0",
            right: "0",
            height: "100%",
            zIndex: "-1",
          }}
        >
          {image.file.contentType === "video/mp4" ? (
            <video
              autoPlay
              loop
              playsInline
              muted
              width="100%"
              height="125%"
              style={borderStyle ? { ...borderStyle } : null}
            >
              <source src={image.file.url} type="video/mp4" />
            </video>
          ) : (
            <GatsbyImage
              image={image.gatsbyImageData}
              alt={image.title || "hero image"}
              sx={{ maxWidth: imageWidth || "100%" }}
              style={borderStyle ? { ...borderStyle } : null}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

export default SingleImageLayout
